import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { PasswordStrength } from "@/components/auth/PasswordStrength";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, User, LogIn, ArrowLeft, Code, CheckCircle2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { validatePassword } from "@/lib/input-validation";
import { SEOHead } from "@/components/seo/SEOHead";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { signUp } = useAuth();
  const { toast } = useToast();

  // Track mouse position for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Validate password on change
  useEffect(() => {
    if (!password) {
      setPasswordError(null);
      return;
    }

    const validation = validatePassword(password);
    if (!validation.valid) {
      setPasswordError(validation.message || null);
    } else {
      setPasswordError(null);
    }
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptTerms) {
      toast({
        title: "Terms and Conditions",
        description: "Please accept the terms and conditions to continue.",
        variant: "destructive",
      });
      return;
    }

    if (passwordError) {
      toast({
        title: "Invalid Password",
        description: passwordError,
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    try {
      await signUp(email, password, firstName, lastName);
      toast({
        title: "Account created!",
        description: "Your account has been successfully created.",
      });
    } catch (error) {
      console.error("Registration error:", error);
      toast({
        title: "Error creating account",
        description: "There was a problem creating your account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEOHead
        title="Create an account | LaunchGuide.io"
        description="Join LaunchGuide and start marketing your products with playbooks and AI-powered tools."
        pathname="/auth/register"
        noindex
      />
      {/* Left side - Image/Illustration */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 relative overflow-hidden">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{ 
            backgroundImage: "linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px), linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}></div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <motion.div 
            className="max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative mb-8">
              {/* Glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-xl opacity-20 blur-xl"></div>
              
              <div className="relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 shadow-2xl">
                <img
                  src="/images/dashboard-preview.svg"
                  alt="LaunchGuide Dashboard"
                  className="w-full h-auto"
                  onError={(e) => {
                    e.currentTarget.src = "https://placehold.co/800x450/f8fafc/64748b?text=LaunchGuide+Dashboard";
                  }}
                />
              </div>
            </div>
            
            <motion.div 
              className="bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg border border-zinc-200 dark:border-zinc-700 relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="absolute top-0 left-0 w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-br-[100px] -z-10"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-tl-[100px] -z-10"></div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-zinc-900 dark:text-white">Launch faster</h3>
                </div>
                <p className="text-zinc-600 dark:text-zinc-300">
                  Get your product to market with professional marketing materials in minutes, not weeks.
                </p>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-zinc-900 dark:text-white">Save resources</h3>
                </div>
                <p className="text-zinc-600 dark:text-zinc-300">
                  No need to hire expensive marketing agencies or dedicate engineering time to marketing tasks.
                </p>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-zinc-900 dark:text-white">Focus on building</h3>
                </div>
                <p className="text-zinc-600 dark:text-zinc-300">
                  Spend your time building great products while LaunchGuide handles your go-to-market strategy.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        {/* Floating elements */}
        <motion.div 
          className="absolute top-1/4 left-[15%] w-64 h-64 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 right-[10%] w-72 h-72 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-3xl"
          animate={{ 
            y: [0, -40, 0],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        <div className="w-full max-w-md space-y-8 relative z-10">
          <div className="absolute top-0 left-0 -mt-16">
            <Button variant="ghost" size="sm" asChild className="group">
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
                Back to Home
              </Link>
            </Button>
          </div>

          <motion.div 
            className="text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mx-auto w-12 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 flex items-center justify-center text-white">
              <Code className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-bold">Create an account</h1>
            <p className="text-zinc-600 dark:text-zinc-300">
              Join LaunchGuide and start marketing your products like a pro
            </p>
          </motion.div>

          <motion.form 
            onSubmit={handleSubmit} 
            className="space-y-6 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">First name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="pl-10 h-12 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">Last name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="pl-10 h-12 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 border-zinc-200 dark:border-zinc-700 focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <PasswordInput
                value={password}
                onChange={setPassword}
                placeholder="Create a secure password"
                error={passwordError}
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Password must be at least 8 characters long with a mix of letters, numbers, and symbols
              </p>
              {password && <PasswordStrength password={password} />}
            </div>

            <div className="flex items-start space-x-2 mt-4">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
                className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="terms"
                  className="text-sm text-zinc-600 dark:text-zinc-300 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{" "}
                  <Link
                    to="/terms-of-service"
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 relative group overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={loading}
            >
              <span className="relative z-10 flex items-center justify-center">
                {loading ? (
                  "Creating account..."
                ) : (
                  <>
                    <LogIn className="mr-2 h-5 w-5" />
                    Create Account
                  </>
                )}
              </span>
              <span className="spotlight" style={{ 
                '--x': `${mousePosition.x}px`,
                '--y': `${mousePosition.y}px`,
              } as React.CSSProperties}></span>
            </Button>
          </motion.form>

          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Already have an account?{" "}
              <Link
                to="/auth/login"
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline"
              >
                Sign in
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
